import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { get, post, patch, del, getErrorMessage } from '../api/client';
import PageHeader from '../components/PageHeader';
import HotIssuesSection from '../components/HotIssuesSection';
import { UPCOMING_VIEW, eventsCalendar, eventsPanel, parseEventsView, toDateStr } from '../utils/meetingsEventsView';
import './MeetingsPage.css';

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const MONTH_ABBREV = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateShort = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatBatchLabel = (b, templates) => {
  const tmpl = templates?.[b.templateKey]?.label || b.templateKey;
  const parts = [tmpl];
  if (b.personTeam) parts.push(b.personTeam);
  if (b.location) parts.push(b.location);
  if (b.topic) parts.push(b.topic);
  const detail = parts.filter(Boolean).join(' • ');
  const range = b.from && b.to ? ` (${formatDateShort(b.from)}–${formatDateShort(b.to)})` : '';
  const count = b.count != null ? ` — ${b.count} event${b.count !== 1 ? 's' : ''}` : '';
  return `${detail}${range}${count}`;
};

const formatOrphanLabel = (og, templates) => {
  const tmpl = templates?.[og.templateKey]?.label || og.templateKey;
  const parts = [tmpl];
  if (og.personTeam) parts.push(og.personTeam);
  if (og.location) parts.push(og.location);
  if (og.topic) parts.push(og.topic);
  const detail = parts.filter(Boolean).join(' • ');
  const range = og.from && og.to ? ` (${formatDateShort(og.from)}–${formatDateShort(og.to)})` : '';
  const count = og.count != null ? ` — ${og.count} event${og.count !== 1 ? 's' : ''}` : '';
  return `${detail}${range}${count}`;
};

const hexToRgba = (hex, alpha) => {
  if (!hex || !hex.startsWith('#')) return 'transparent';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// Generate recurring dates based on frequency
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const toDayIndex = (v) => {
  if (v == null) return undefined;
  const idx = typeof v === 'string' ? DAYS.indexOf(v) : v;
  return idx >= 0 ? idx : undefined;
};

const generateRecurringDates = (startStr, endStr, frequency, dayOfWeek, dayOfMonth, skipNovDec) => {
  const from = new Date(startStr + 'T12:00:00');
  const to = new Date(endStr + 'T23:59:59');
  const results = [];
  if (frequency === 'weekly') {
    const targetDay = (dayOfWeek != null ? toDayIndex(dayOfWeek) : from.getDay()) ?? from.getDay();
    let d = new Date(from);
    if (d.getDay() !== targetDay) {
      d.setDate(d.getDate() + ((targetDay - d.getDay() + 7) % 7));
    }
    while (d <= to) {
      if (!skipNovDec || (d.getMonth() !== 10 && d.getMonth() !== 11)) results.push(new Date(d));
      d.setDate(d.getDate() + 7);
    }
  } else if (frequency === 'biweekly') {
    const targetDay = (dayOfWeek != null ? toDayIndex(dayOfWeek) : from.getDay()) ?? from.getDay();
    let d = new Date(from);
    if (d.getDay() !== targetDay) {
      d.setDate(d.getDate() + ((targetDay - d.getDay() + 7) % 7));
    }
    while (d <= to) {
      if (!skipNovDec || (d.getMonth() !== 10 && d.getMonth() !== 11)) results.push(new Date(d));
      d.setDate(d.getDate() + 14);
    }
  } else if (frequency === 'monthly') {
    const dom = dayOfMonth ?? from.getDate();
    let y = from.getFullYear();
    let m = from.getMonth();
    while (y < to.getFullYear() || (y === to.getFullYear() && m <= to.getMonth())) {
      const lastDay = new Date(y, m + 1, 0).getDate();
      const d = new Date(y, m, Math.min(dom, lastDay));
      if (d >= from && d <= to && (!skipNovDec || (d.getMonth() !== 10 && d.getMonth() !== 11))) results.push(d);
      m++;
      if (m > 11) { m = 0; y++; }
    }
  } else if (frequency === '4th-weekday') {
    const targetDay = (dayOfWeek != null ? toDayIndex(dayOfWeek) : 6) ?? 6;
    let y = from.getFullYear();
    let m = from.getMonth();
    while (y < to.getFullYear() || (y === to.getFullYear() && m <= to.getMonth())) {
      if (skipNovDec && (m === 10 || m === 11)) { m++; if (m > 11) { m = 0; y++; } continue; }
      let count = 0;
      for (let day = 1; day <= 28; day++) {
        const d = new Date(y, m, day);
        if (d.getDay() === targetDay) {
          count++;
          if (count === 4 && d >= from && d <= to) results.push(d);
        }
      }
      m++;
      if (m > 11) { m = 0; y++; }
    }
  }
  return results;
};

const LEADER_DEV_ROLES = ['Testimony', 'Open Share Leader', 'Host', 'Chip Script', 'Team+', 'New Beginnings Ldr'];
const LEADER_DEV_STATUSES = ['Interested', 'Contacted', 'Training', 'Evaluated', 'Paused', 'Certified'];
const LEADER_DEV_PIPELINE = ['Early', 'Middle', 'Final'];

const getDefaultRecurringForm = () => ({
  startDate: toDateStr(new Date()),
  endDate: toDateStr(new Date(Date.now() + 56 * 24 * 60 * 60 * 1000)),
  frequency: 'weekly',
  dayOfWeek: 'Mon',
  dayOfMonth: 15,
  skipNovDec: false,
  templateKey: 'cr-lesson',
  personTeam: '',
  location: '',
  topic: '',
  notes: '',
});

const MeetingsPage = () => {
  const [templates, setTemplates] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [templateKey, setTemplateKey] = useState('cr-lesson');
  const [date, setDate] = useState(toDateStr(new Date()));
  const [personTeam, setPersonTeam] = useState('');
  const [location, setLocation] = useState('');
  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [manageTemplatesOpen, setManageTemplatesOpen] = useState(false);
  const [eventsView, setEventsView] = useState(UPCOMING_VIEW);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [recurringForm, setRecurringForm] = useState(getDefaultRecurringForm);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [batches, setBatches] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [postponeOpen, setPostponeOpen] = useState(false);
  const [postponeForm, setPostponeForm] = useState({ fromDate: toDateStr(new Date()), direction: 'later', weeks: 1, addModified: true });
  const [postponePreview, setPostponePreview] = useState(null);
  const [postponeMessage, setPostponeMessage] = useState('');
  const [cascadeMessage, setCascadeMessage] = useState('');
  const [stepStudyCohorts, setStepStudyCohorts] = useState([]);
  const [stepStudyOpen, setStepStudyOpen] = useState(false);
  const [editingStepStudyId, setEditingStepStudyId] = useState(null);
  const [stepStudyEditForm, setStepStudyEditForm] = useState({});
  const [servantTrainingOpen, setServantTrainingOpen] = useState(false);
  const [servantTrainingYear, setServantTrainingYear] = useState(new Date().getFullYear());
  const [servantTrainingEntries, setServantTrainingEntries] = useState([]);
  const [servantTrainingLoading, setServantTrainingLoading] = useState(false);
  const [editingServantTrainingId, setEditingServantTrainingId] = useState(null);
  const [servantTrainingEditForm, setServantTrainingEditForm] = useState({});
  const [servantTrainingMessage, setServantTrainingMessage] = useState('');
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  const [completedArchiveOpen, setCompletedArchiveOpen] = useState(false);
  const [addingProject, setAddingProject] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [projectForm, setProjectForm] = useState({});
  const [leaderDevOpen, setLeaderDevOpen] = useState(false);
  const [leaderDev, setLeaderDev] = useState([]);
  const [leaderDevLoading, setLeaderDevLoading] = useState(false);
  const [leaderDevRoleFilter, setLeaderDevRoleFilter] = useState('');
  const [addingLeaderDev, setAddingLeaderDev] = useState(false);
  const [editingLeaderDevId, setEditingLeaderDevId] = useState(null);
  const [leaderDevForm, setLeaderDevForm] = useState({});

  /** Empty pipeline first; then Early → Middle → Final; tie-break by name */
  const sortedLeaderDev = useMemo(() => {
    const pipelineRank = (stage) => {
      const s = String(stage ?? '').trim();
      if (!s) return 0;
      const i = LEADER_DEV_PIPELINE.indexOf(s);
      if (i >= 0) return i + 1;
      return LEADER_DEV_PIPELINE.length + 1;
    };
    return [...leaderDev].sort((a, b) => {
      const ra = pipelineRank(a.pipelineStage);
      const rb = pipelineRank(b.pipelineStage);
      if (ra !== rb) return ra - rb;
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
    });
  }, [leaderDev]);

  const fetchConfig = useCallback(async () => {
    const { ok, data } = await get('/api/meetings/config');
    if (ok && data?.templates) setTemplates(data.templates);
  }, []);

  const eventsFetchGen = useRef(0);
  const fetchEvents = useCallback(async () => {
    const gen = ++eventsFetchGen.current;
    const { from, to } = eventsCalendar().fetchRange;
    const { ok, data } = await get(`/api/meetings/events?from=${from}&to=${to}`);
    if (gen !== eventsFetchGen.current) return;
    if (ok && Array.isArray(data)) setEvents(data);
  }, []);

  const fetchBatches = useCallback(async () => {
    const { ok, data } = await get('/api/meetings/batches');
    if (ok && data) {
      setBatches(Array.isArray(data.batches) ? data.batches : (Array.isArray(data) ? data : []));
      setOrphanGroups(Array.isArray(data?.orphanGroups) ? data.orphanGroups : []);
    }
  }, []);

  const fetchStepStudyCohorts = useCallback(async () => {
    try {
      const { ok, data } = await get('/api/meetings/step-study/cohorts');
      if (ok) {
        const arr = Array.isArray(data) ? data : (data?.cohorts ?? []);
        setStepStudyCohorts(arr);
      }
    } catch (err) {
      console.error('fetchStepStudyCohorts:', err);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    setProjectsError('');
    try {
      const { ok, data } = await get('/api/meetings/projects');
      const list = Array.isArray(data) ? data : (data?.projects || []);
      if (ok) {
        setProjects(list);
      } else {
        setProjectsError(data?.message || 'Failed to load projects. Check your connection and try again.');
      }
    } catch (err) {
      setProjectsError('Failed to load projects.');
    }
  }, []);

  const fetchServantTrainingSchedule = useCallback(async (year) => {
    const y = year ?? servantTrainingYear;
    const { ok, data } = await get(`/api/meetings/servant-training-schedule?year=${y}`);
    if (ok && Array.isArray(data?.entries)) setServantTrainingEntries(data.entries);
    else setServantTrainingEntries([]);
  }, [servantTrainingYear]);

  const fetchLeaderDevelopment = useCallback(async () => {
    try {
      const url = leaderDevRoleFilter
        ? `/api/meetings/leader-development?role=${encodeURIComponent(leaderDevRoleFilter)}`
        : '/api/meetings/leader-development';
      const { ok, data } = await get(url);
      setLeaderDev(ok && Array.isArray(data) ? data : []);
    } catch (err) {
      setLeaderDev([]);
    }
  }, [leaderDevRoleFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([
        fetchConfig(),
        fetchEvents(),
        fetchStepStudyCohorts(),
        fetchProjects(),
        fetchServantTrainingSchedule(),
        fetchLeaderDevelopment(),
      ]);
    } catch (err) {
      setError('Failed to load meetings data.');
    } finally {
      setLoading(false);
    }
  }, [fetchConfig, fetchEvents, fetchStepStudyCohorts, fetchProjects, fetchServantTrainingSchedule, fetchLeaderDevelopment]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (recurringOpen) {
      setBatchesLoading(true);
      fetchBatches().finally(() => setBatchesLoading(false));
    }
  }, [recurringOpen, fetchBatches]);

  useEffect(() => {
    if (recurringOpen && !editingBatchId) {
      setRecurringForm(getDefaultRecurringForm());
    }
  }, [recurringOpen, editingBatchId]);

  useEffect(() => {
    if (servantTrainingOpen) {
      setServantTrainingMessage('');
      setServantTrainingLoading(true);
      setEditingServantTrainingId(null);
      setServantTrainingEditForm({});
      fetchServantTrainingSchedule(servantTrainingYear).finally(() => setServantTrainingLoading(false));
    }
  }, [servantTrainingOpen, servantTrainingYear, fetchServantTrainingSchedule]);

  useEffect(() => {
    if (projectsOpen) {
      setProjectsError('');
      setProjectsLoading(true);
      fetchProjects().finally(() => setProjectsLoading(false));
    }
  }, [projectsOpen, fetchProjects]);

  useEffect(() => {
    if (leaderDevOpen) {
      setLeaderDevLoading(true);
      fetchLeaderDevelopment().finally(() => setLeaderDevLoading(false));
    }
  }, [leaderDevOpen, leaderDevRoleFilter, fetchLeaderDevelopment]);

  // Polling every 20 seconds when tab is visible
  useEffect(() => {
    if (!('visibilityState' in document)) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchEvents();
    }, 20000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  const handleAdd = async (e) => {
    e.preventDefault();
    // Adding a Modified Schedule on a date that already has a meeting pushes
    // that meeting and everything after it a week later — warn before doing it.
    if (templateKey === 'modified-schedule' && events.some((ev) => toDateStr(ev.date) === date)) {
      if (!window.confirm(`There is already a meeting on ${formatDateShort(date)}. It and every meeting after it will move one week later. Continue?`)) return;
    }
    setError('');
    setCascadeMessage('');
    setSubmitting(true);
    try {
      const { ok, status, data } = await post('/api/meetings/events', {
        templateKey,
        date,
        personTeam: personTeam || '',
        location: location || '',
        topic: topic || '',
        notes: notes || '',
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to add event.'));
        return;
      }
      const parts = [];
      if (data?.shiftedFollowing > 0) parts.push(`moved ${data.shiftedFollowing} meeting${data.shiftedFollowing !== 1 ? 's' : ''} one week later`);
      if (data?.realternated > 0) parts.push(`re-alternated ${data.realternated} following Lesson/Testimonial week${data.realternated !== 1 ? 's' : ''}`);
      if (data?.lessonsRenumbered > 0) parts.push(`updated ${data.lessonsRenumbered} lesson topic${data.lessonsRenumbered !== 1 ? 's' : ''} to stay in curriculum order`);
      if (parts.length) setCascadeMessage(`Added and automatically ${parts.join(' and ')}.`);
      setPersonTeam('');
      setLocation('');
      setTopic('');
      setNotes('');
      await fetchEvents();
    } catch (err) {
      setError('Failed to add event.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (ev) => {
    setEditingId(ev._id);
    setEditForm({
      templateKey: ev.templateKey,
      date: toDateStr(ev.date),
      personTeam: ev.personTeam || '',
      location: ev.location || '',
      topic: ev.topic || '',
      notes: ev.notes || '',
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    // Changing a week's type between CR Lesson / Testimonial (or away from one
    // of them) automatically re-alternates the rest of the schedule.
    const original = events.find((ev) => ev._id === editingId);
    const alternating = ['cr-lesson', 'testimonial'];
    if (
      original &&
      editForm.templateKey !== original.templateKey &&
      (alternating.includes(original.templateKey) || alternating.includes(editForm.templateKey))
    ) {
      const fromLabel = templates[original.templateKey]?.label || original.templateKey;
      const toLabel = templates[editForm.templateKey]?.label || editForm.templateKey;
      if (!window.confirm(`Changing ${fromLabel} to ${toLabel} will automatically re-alternate all following Lesson/Testimonial weeks. Continue?`)) return;
    }
    setError('');
    setCascadeMessage('');
    try {
      const { ok, status, data } = await patch(`/api/meetings/events/${editingId}`, {
        ...editForm,
        date: editForm.date,
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to update event.'));
        return;
      }
      const parts = [];
      if (data?.realternated > 0) parts.push(`re-alternated ${data.realternated} following Lesson/Testimonial week${data.realternated !== 1 ? 's' : ''}`);
      if (data?.lessonsRenumbered > 0) parts.push(`updated ${data.lessonsRenumbered} lesson topic${data.lessonsRenumbered !== 1 ? 's' : ''} to stay in curriculum order`);
      if (parts.length) setCascadeMessage(`Saved and automatically ${parts.join(' and ')}.`);
      setEditingId(null);
      await fetchEvents();
    } catch (err) {
      setError('Failed to update event.');
    }
  };

  const handleDelete = async (ev) => {
    if (!window.confirm('Delete this event?')) return;
    const { ok } = await del(`/api/meetings/events/${ev._id}`);
    if (ok) {
      setEditingId(null);
      await fetchEvents();
    }
  };

  const fetchPostponePreview = useCallback(async (form) => {
    if (!form.fromDate) {
      setPostponePreview(null);
      return;
    }
    try {
      const { ok, data } = await post('/api/meetings/events/shift', {
        fromDate: form.fromDate,
        weeks: form.weeks,
        direction: form.direction,
        preview: true,
      });
      setPostponePreview(ok ? (data?.shifted ?? 0) : null);
    } catch (err) {
      setPostponePreview(null);
    }
  }, []);

  useEffect(() => {
    if (postponeOpen) fetchPostponePreview(postponeForm);
  }, [postponeOpen, postponeForm, fetchPostponePreview]);

  const handlePostpone = async (e) => {
    e.preventDefault();
    if (!postponeForm.fromDate) {
      setError('Pick a date to shift from.');
      return;
    }
    const dirWord = postponeForm.direction === 'earlier' ? 'earlier' : 'later';
    const weekWord = postponeForm.weeks === 1 ? 'a week' : `${postponeForm.weeks} weeks`;
    if (!window.confirm(`Move every meeting on or after ${formatDateShort(postponeForm.fromDate)} ${weekWord} ${dirWord}?`)) return;
    setError('');
    setPostponeMessage('');
    setSubmitting(true);
    try {
      const { ok, status, data } = await post('/api/meetings/events/shift', {
        fromDate: postponeForm.fromDate,
        weeks: postponeForm.weeks,
        direction: postponeForm.direction,
        addModified: postponeForm.direction === 'later' && postponeForm.addModified,
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to shift schedule.'));
        return;
      }
      const parts = [`Moved ${data?.shifted ?? 0} meeting${(data?.shifted ?? 0) !== 1 ? 's' : ''} ${weekWord} ${dirWord}`];
      if (data?.addedModified) parts.push(`added Modified Schedule on ${formatDateShort(postponeForm.fromDate)}`);
      setPostponeMessage(`${parts.join('; ')}.`);
      await fetchEvents();
      await fetchBatches();
    } catch (err) {
      setError('Failed to shift schedule.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOption = async (templateKey, field, value, onSuccess) => {
    if (!window.confirm(`Remove "${value}" from ${templateKey}?`)) return;
    setError('');
    try {
      const { ok, status, data } = await del('/api/meetings/config/options', { templateKey, field, value });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to remove option.'));
        return;
      }
      if (data?.templates) setTemplates(data.templates);
      onSuccess?.();
    } catch (err) {
      setError('Failed to remove option.');
    }
  };

  const handleEditOption = async (templateKey, field, currentValue) => {
    const label = { personTeam: 'Person/Team', location: 'Location', topic: 'Topic' }[field];
    const newValue = window.prompt(`Edit ${label}:`, currentValue);
    if (newValue == null || newValue.trim() === '') return;
    if (newValue.trim() === currentValue) return;
    setError('');
    try {
      const { ok, status, data } = await patch('/api/meetings/config/options', {
        templateKey,
        field,
        oldValue: currentValue,
        newValue: newValue.trim(),
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to edit option.'));
        return;
      }
      if (data?.templates) {
        setTemplates(data.templates);
        setError('');
        await fetchEvents();
      }
    } catch (err) {
      setError('Failed to edit option.');
    }
  };

  const handleReorderOptions = async (templateKey, field, orderedValues) => {
    setError('');
    try {
      const { ok, status, data } = await patch('/api/meetings/config/options/reorder', {
        templateKey,
        field,
        orderedValues,
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to reorder options.'));
        return;
      }
      if (data?.templates) setTemplates(data.templates);
    } catch (err) {
      setError('Failed to reorder options.');
    }
  };

  const moveOption = (opts, fromIdx, direction) => {
    const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
    if (toIdx < 0 || toIdx >= opts.length) return opts;
    const next = [...opts];
    [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
    return next;
  };

  const recurringPreview = recurringForm.startDate && recurringForm.endDate
    ? generateRecurringDates(
        recurringForm.startDate,
        recurringForm.endDate,
        recurringForm.frequency,
        recurringForm.frequency !== 'monthly' ? recurringForm.dayOfWeek : undefined,
        recurringForm.frequency === 'monthly' ? recurringForm.dayOfMonth : undefined,
        recurringForm.skipNovDec
      )
    : [];

  const handleCreateRecurring = async (e) => {
    e.preventDefault();
    if (recurringPreview.length === 0) {
      setError('No dates in range. Adjust start/end or frequency.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const events = recurringPreview.map((d) => ({
        templateKey: recurringForm.templateKey,
        date: toDateStr(d),
        personTeam: recurringForm.personTeam || '',
        location: recurringForm.location || '',
        topic: recurringForm.topic || '',
        notes: recurringForm.notes || '',
      }));
      const { ok, status, data } = await post('/api/meetings/events/bulk', { events });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to create recurring events.'));
        return;
      }
      setRecurringForm({ ...getDefaultRecurringForm(), startDate: '', endDate: '' });
      await fetchEvents();
      await fetchBatches();
    } catch (err) {
      setError('Failed to create recurring events.');
    } finally {
      setSubmitting(false);
    }
  };

  const recurringFormRef = useRef(null);

  const startEditBatch = (b) => {
    setEditingBatchId(b.batchId);
    const fromStr = b.from ? toDateStr(b.from) : '';
    const toStr = b.to ? toDateStr(b.to) : '';
    setRecurringForm({
      ...getDefaultRecurringForm(),
      startDate: fromStr,
      endDate: toStr,
      templateKey: b.templateKey,
      personTeam: b.personTeam || '',
      location: b.location || '',
      topic: b.topic || '',
      notes: b.notes || '',
    });
    setTimeout(() => recurringFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleUpdateBatch = async (e) => {
    e.preventDefault();
    if (!editingBatchId) return;
    if (!recurringForm.startDate || !recurringForm.endDate) {
      setError('Start date and end date are required.');
      return;
    }
    if (recurringPreview.length === 0) {
      setError('No dates in range. Adjust start/end or frequency.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const { ok, status, data } = await patch(`/api/meetings/events/by-batch/${editingBatchId}`, {
        startDate: recurringForm.startDate,
        endDate: recurringForm.endDate,
        frequency: recurringForm.frequency,
        dayOfWeek: recurringForm.frequency !== 'monthly' ? toDayIndex(recurringForm.dayOfWeek) : undefined,
        dayOfMonth: recurringForm.frequency === 'monthly' ? recurringForm.dayOfMonth : undefined,
        skipNovDec: recurringForm.skipNovDec,
        templateKey: recurringForm.templateKey,
        personTeam: recurringForm.personTeam || '',
        location: recurringForm.location || '',
        topic: recurringForm.topic || '',
        notes: recurringForm.notes || '',
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to update series.'));
        return;
      }
      setEditingBatchId(null);
      setRecurringForm(getDefaultRecurringForm());
      await fetchBatches();
      await fetchEvents();
    } catch (err) {
      setError('Failed to update series.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Delete all events in this recurring series? This cannot be undone.')) return;
    setError('');
    try {
      const { ok, status, data } = await del(`/api/meetings/events/by-batch/${batchId}`);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to delete series.'));
        return;
      }
      if (editingBatchId === batchId) {
        setEditingBatchId(null);
        setRecurringForm(getDefaultRecurringForm());
      }
      await fetchBatches();
      await fetchEvents();
    } catch (err) {
      setError('Failed to delete series.');
    }
  };

  const handleConvertToSeries = async (signature) => {
    setError('');
    setSubmitting(true);
    try {
      const { ok, status, data } = await post('/api/meetings/events/convert-to-series', { signature });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to create series.'));
        return;
      }
      await fetchBatches();
      await fetchEvents();
    } catch (err) {
      setError('Failed to create series.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateServantTraining = async () => {
    setError('');
    setServantTrainingMessage('');
    setSubmitting(true);
    try {
      const { ok, status, data } = await post('/api/meetings/servant-training-schedule/generate', {
        year: servantTrainingYear,
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to create schedule.'));
        return;
      }
      setServantTrainingEntries(data?.entries || []);
      await fetchEvents();
      if (data?.created === 0) {
        setServantTrainingMessage(`A schedule for ${servantTrainingYear} already exists. Displaying it below. Edit the entries to make changes.`);
      }
    } catch (err) {
      setError('Failed to create schedule.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditServantTraining = (entry) => {
    setEditingServantTrainingId(entry._id);
    setServantTrainingEditForm({
      date: toDateStr(entry.date),
      startTime: entry.startTime || '10:30',
      endTime: entry.endTime || '12:30',
      location: entry.location || 'C110',
      topic: entry.topic || '',
      trainer: entry.trainer || 'TBD',
    });
  };

  const handleSaveServantTrainingEdit = async (e) => {
    e.preventDefault();
    if (!editingServantTrainingId) return;
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        date: servantTrainingEditForm.date,
        startTime: servantTrainingEditForm.startTime,
        endTime: servantTrainingEditForm.endTime,
        location: servantTrainingEditForm.location,
        topic: servantTrainingEditForm.topic,
        trainer: servantTrainingEditForm.trainer,
      };
      const { ok, status, data } = await patch(`/api/meetings/servant-training-schedule/${editingServantTrainingId}`, payload);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to update schedule.'));
        return;
      }
      setEditingServantTrainingId(null);
      setServantTrainingEditForm({});
      await fetchServantTrainingSchedule();
      await fetchEvents();
    } catch (err) {
      setError('Failed to update schedule.');
    } finally {
      setSubmitting(false);
    }
  };

  const getDefaultProjectForm = () => ({
    projectName: '',
    shortDescription: '',
    ministryArea: '',
    projectType: '',
    projectOwner: '',
    status: 'Idea',
    priorityLevel: 'Medium',
    percentComplete: 0,
    projectStartDate: '',
    targetCompletionDate: '',
    actualCompletionDate: '',
    nextReviewDate: '',
    crAreaImpacted: [],
    budgetNeeded: '',
    resourcesNeeded: '',
    outsideSupportRequired: '',
    lastUpdateDate: '',
    nextActionStep: '',
    notes: '',
    finalOutcome: '',
    lessonsLearned: '',
  });

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!projectForm.projectName?.trim()) {
      setError('Project name is required.');
      return;
    }
    setError('');
    setProjectsError('');
    setSubmitting(true);
    try {
      const payload = { ...projectForm };
      ['projectStartDate', 'targetCompletionDate', 'actualCompletionDate', 'nextReviewDate', 'lastUpdateDate'].forEach((f) => {
        if (payload[f] === '') payload[f] = null;
      });
      const { ok, status, data } = await post('/api/meetings/projects', payload);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to add project.'));
        return;
      }
      setAddingProject(false);
      setProjectForm(getDefaultProjectForm());
      if (data && data._id) {
        setProjects((prev) => [data, ...prev]);
      } else {
        await fetchProjects();
      }
    } catch (err) {
      setError('Failed to add project.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditProject = (p) => {
    setEditingProjectId(p._id);
    setProjectForm({
      ...getDefaultProjectForm(),
      ...p,
      projectStartDate: p.projectStartDate ? toDateStr(p.projectStartDate) : '',
      targetCompletionDate: p.targetCompletionDate ? toDateStr(p.targetCompletionDate) : '',
      actualCompletionDate: p.actualCompletionDate ? toDateStr(p.actualCompletionDate) : '',
      nextReviewDate: p.nextReviewDate ? toDateStr(p.nextReviewDate) : '',
      lastUpdateDate: p.lastUpdateDate ? toDateStr(p.lastUpdateDate) : '',
      crAreaImpacted: p.crAreaImpacted || [],
    });
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!editingProjectId) return;
    if (!projectForm.projectName?.trim()) {
      setError('Project name is required.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const payload = { ...projectForm };
      delete payload._id;
      delete payload.createdAt;
      delete payload.updatedAt;
      ['projectStartDate', 'targetCompletionDate', 'actualCompletionDate', 'nextReviewDate', 'lastUpdateDate'].forEach((f) => {
        if (payload[f] === '') payload[f] = null;
      });
      const { ok, status, data } = await patch(`/api/meetings/projects/${editingProjectId}`, payload);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to update project.'));
        return;
      }
      setEditingProjectId(null);
      setProjectForm(getDefaultProjectForm());
      await fetchProjects();
    } catch (err) {
      setError('Failed to update project.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    setError('');
    try {
      const { ok } = await del(`/api/meetings/projects/${id}`);
      if (!ok) {
        setError('Failed to delete project.');
        return;
      }
      if (editingProjectId === id) {
        setEditingProjectId(null);
        setProjectForm(getDefaultProjectForm());
      }
      await fetchProjects();
    } catch (err) {
      setError('Failed to delete project.');
    }
  };

  const getDefaultLeaderDevForm = () => ({
    name: '',
    roleBeingDevelopedFor: '',
    status: 'Contacted',
    pipelineStage: '',
    dateContacted: '',
    trainingDate: '',
    evaluationDate: '',
    mentorLeaderOverseeing: '',
    notes: '',
  });

  const handleAddLeaderDev = async (e) => {
    e.preventDefault();
    if (!leaderDevForm.name?.trim()) {
      setError('Name is required.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const payload = { ...leaderDevForm };
      ['dateContacted', 'trainingDate', 'evaluationDate'].forEach((f) => {
        if (payload[f] === '') payload[f] = null;
      });
      const { ok, status, data } = await post('/api/meetings/leader-development', payload);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to add servant volunteer.'));
        return;
      }
      setAddingLeaderDev(false);
      setLeaderDevForm(getDefaultLeaderDevForm());
      await fetchLeaderDevelopment();
    } catch (err) {
      setError('Failed to add servant volunteer.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEditLeaderDev = (entry) => {
    setEditingLeaderDevId(entry._id);
    setLeaderDevForm({
      name: entry.name || '',
      roleBeingDevelopedFor: entry.roleBeingDevelopedFor || '',
      status: entry.status || 'Contacted',
      pipelineStage: entry.pipelineStage || '',
      dateContacted: entry.dateContacted ? toDateStr(entry.dateContacted) : '',
      trainingDate: entry.trainingDate ? toDateStr(entry.trainingDate) : '',
      evaluationDate: entry.evaluationDate ? toDateStr(entry.evaluationDate) : '',
      mentorLeaderOverseeing: entry.mentorLeaderOverseeing || '',
      notes: entry.notes || '',
    });
  };

  const handleUpdateLeaderDev = async (e) => {
    e.preventDefault();
    if (!editingLeaderDevId) return;
    if (!leaderDevForm.name?.trim()) {
      setError('Name is required.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const payload = { ...leaderDevForm };
      delete payload._id;
      delete payload.createdAt;
      delete payload.updatedAt;
      ['dateContacted', 'trainingDate', 'evaluationDate'].forEach((f) => {
        if (payload[f] === '') payload[f] = null;
      });
      const { ok, status, data } = await patch(`/api/meetings/leader-development/${editingLeaderDevId}`, payload);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to update.'));
        return;
      }
      setEditingLeaderDevId(null);
      setLeaderDevForm(getDefaultLeaderDevForm());
      await fetchLeaderDevelopment();
    } catch (err) {
      setError('Failed to update.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLeaderDev = async (id) => {
    if (!window.confirm('Remove this servant volunteer from the tracker?')) return;
    setError('');
    try {
      const { ok } = await del(`/api/meetings/leader-development/${id}`);
      if (!ok) {
        setError('Failed to delete.');
        return;
      }
      if (editingLeaderDevId === id) {
        setEditingLeaderDevId(null);
        setLeaderDevForm(getDefaultLeaderDevForm());
      }
      await fetchLeaderDevelopment();
    } catch (err) {
      setError('Failed to delete.');
    }
  };

  const handleAddOption = async (field, currentTemplateKey, onSelect) => {
    const label = { personTeam: 'Person/Team', location: 'Location', topic: 'Topic' }[field];
    const value = window.prompt(`Add new ${label} option:`);
    if (!value || !value.trim()) return;
    setError('');
    try {
      const { ok, status, data } = await post('/api/meetings/config/options', {
        templateKey: currentTemplateKey,
        field,
        value: value.trim(),
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to add option.'));
        return;
      }
      if (data?.templates) setTemplates(data.templates);
      onSelect?.(value.trim());
    } catch (err) {
      setError('Failed to add option.');
    }
  };


  const formatLeaders = (leader1, leader2) => [leader1, leader2].filter(Boolean).join(', ') || '—';

  const startEditStepStudy = (c) => {
    setEditingStepStudyId(c._id);
    setStepStudyEditForm({
      issues: c.issues || '',
      nextStep: c.nextStep || '',
    });
  };

  const handleEditStepStudySave = async (e) => {
    e.preventDefault();
    if (!editingStepStudyId) return;
    setError('');
    setSubmitting(true);
    try {
      const { ok, status, data } = await patch(`/api/meetings/step-study/cohorts/${editingStepStudyId}`, {
        issues: stepStudyEditForm.issues,
        nextStep: stepStudyEditForm.nextStep,
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to update Step Study.'));
        return;
      }
      setEditingStepStudyId(null);
      await fetchStepStudyCohorts();
    } catch (err) {
      setError('Failed to update Step Study.');
    } finally {
      setSubmitting(false);
    }
  };


  const cal = eventsCalendar();
  const panel = eventsPanel(events, eventsView, cal);
  const template = templates[templateKey] || {};
  const personTeamOpts = template.personTeamOptions || [];
  const locationOpts = template.locationOptions || [];
  const topicOpts = template.topicOptions || [];

  if (loading) return <div className="meetings-page"><p>Loading…</p></div>;

  return (
    <div className="meetings-page">
      <PageHeader title="Ministry Meetings" section="leaders" />
      <p className="meetings-subtitle">
        Shows meetings that have not happened yet, through Dec 31. Use Archive to open past weeks. This year's weeks can still be corrected. Earlier years are read-only. Add events during meetings.
      </p>

      <section className="meetings-list-section meetings-events-section">
        <button
          type="button"
          className="meetings-manage-toggle meetings-events-main-toggle"
          onClick={() => setEventsOpen((o) => !o)}
        >
          {eventsOpen ? '▼' : '▶'} Events ({panel.upcomingCount} upcoming)
        </button>
        {eventsOpen && (
          <div className="meetings-events-content">
            <div className="meetings-events-actions">
              <button
                type="button"
                className="meetings-events-link"
                onClick={() => setQuickAddOpen((o) => !o)}
              >
                {quickAddOpen ? '▼' : '▶'} Quick Add
              </button>
              <button
                type="button"
                className="meetings-events-link"
                onClick={() => setRecurringOpen((o) => !o)}
              >
                {recurringOpen ? '▼' : '▶'} Create recurring meetings
              </button>
              <button
                type="button"
                className="meetings-events-link"
                onClick={() => setPostponeOpen((o) => !o)}
              >
                {postponeOpen ? '▼' : '▶'} Postpone schedule
              </button>
              <button
                type="button"
                className="meetings-events-link"
                onClick={() => setManageTemplatesOpen((o) => !o)}
              >
                {manageTemplatesOpen ? '▼' : '▶'} Manage templates
              </button>
            </div>
            {quickAddOpen && (
        <div style={{ marginTop: '0.75rem' }}>
      <form onSubmit={handleAdd} className="meetings-quick-add">
        <div className="meetings-form-row">
          <div className="form-group">
            <label>Template</label>
            <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
              {Object.entries(templates).map(([k, t]) => (
                <option key={k} value={k}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={date}
              min={cal.yearStart}
              max={cal.yearEnd}
              onChange={(e) => setDate(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              required
            />
          </div>
          <div className="form-group">
            <label>Person/Team</label>
            <select
              value={personTeam}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__add_new__') handleAddOption('personTeam', templateKey, setPersonTeam);
                else if (v === '__remove__') handleDeleteOption(templateKey, 'personTeam', personTeam, () => setPersonTeam(''));
                else setPersonTeam(v);
              }}
            >
              <option value="">—</option>
              {personTeamOpts.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
              <option value="__add_new__">+ Add new…</option>
              {personTeam && <option value="__remove__">— Remove from list</option>}
            </select>
          </div>
          <div className="form-group">
            <label>Location</label>
            <select
              value={location}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__add_new__') handleAddOption('location', templateKey, setLocation);
                else if (v === '__remove__') handleDeleteOption(templateKey, 'location', location, () => setLocation(''));
                else setLocation(v);
              }}
            >
              <option value="">—</option>
              {locationOpts.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
              <option value="__add_new__">+ Add new…</option>
              {location && <option value="__remove__">— Remove from list</option>}
            </select>
          </div>
          <div className="form-group">
            <label>Topic</label>
            <select
              value={topic}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__add_new__') handleAddOption('topic', templateKey, setTopic);
                else if (v === '__remove__') handleDeleteOption(templateKey, 'topic', topic, () => setTopic(''));
                else setTopic(v);
              }}
            >
              <option value="">—</option>
              {topicOpts.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
              <option value="__add_new__">+ Add new…</option>
              {topic && <option value="__remove__">— Remove from list</option>}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Notes</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes"
          />
        </div>
        {error && <p className="meetings-error">{error}</p>}
        <button type="submit" disabled={submitting}>
          {submitting ? 'Adding…' : 'Add Event'}
        </button>
      </form>
        </div>
            )}
        {recurringOpen && (
          <div className="meetings-recurring-content" ref={recurringFormRef}>
            <form onSubmit={editingBatchId ? handleUpdateBatch : handleCreateRecurring} className="meetings-recurring-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Template</label>
                  <select
                    value={recurringForm.templateKey}
                    onChange={(e) => setRecurringForm({ ...recurringForm, templateKey: e.target.value })}
                    disabled={!!editingBatchId}
                  >
                    {Object.entries(templates).map(([k, t]) => (
                      <option key={k} value={k}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Start date</label>
                  <input
                    type="date"
                    value={recurringForm.startDate}
                    onChange={(e) => setRecurringForm({ ...recurringForm, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End date</label>
                  <input
                    type="date"
                    value={recurringForm.endDate}
                    onChange={(e) => setRecurringForm({ ...recurringForm, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Frequency</label>
                  <select
                    value={recurringForm.frequency}
                    onChange={(e) => setRecurringForm({ ...recurringForm, frequency: e.target.value })}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                {recurringForm.frequency !== 'monthly' && (
                  <div className="form-group">
                    <label>Day of week</label>
                    <select
                      value={recurringForm.dayOfWeek}
                      onChange={(e) => setRecurringForm({ ...recurringForm, dayOfWeek: e.target.value })}
                    >
                      {DAYS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                )}
                {recurringForm.frequency === 'monthly' && (
                  <div className="form-group">
                    <label>Day of month</label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={recurringForm.dayOfMonth}
                      onChange={(e) => setRecurringForm({ ...recurringForm, dayOfMonth: parseInt(e.target.value, 10) || 15 })}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label className="meetings-checkbox-label">
                    <input
                      type="checkbox"
                      checked={recurringForm.skipNovDec}
                      onChange={(e) => setRecurringForm({ ...recurringForm, skipNovDec: e.target.checked })}
                    />
                    Skip Nov & Dec
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Person/Team</label>
                  <select
                    value={recurringForm.personTeam}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__add_new__') handleAddOption('personTeam', recurringForm.templateKey, (val) => setRecurringForm({ ...recurringForm, personTeam: val }));
                      else if (v === '__remove__') handleDeleteOption(recurringForm.templateKey, 'personTeam', recurringForm.personTeam, () => setRecurringForm({ ...recurringForm, personTeam: '' }));
                      else setRecurringForm({ ...recurringForm, personTeam: v });
                    }}
                  >
                    <option value="">—</option>
                    {(templates[recurringForm.templateKey]?.personTeamOptions || []).map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                    <option value="__add_new__">+ Add new…</option>
                    {recurringForm.personTeam && <option value="__remove__">— Remove from list</option>}
                  </select>
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <select
                    value={recurringForm.location}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__add_new__') handleAddOption('location', recurringForm.templateKey, (val) => setRecurringForm({ ...recurringForm, location: val }));
                      else if (v === '__remove__') handleDeleteOption(recurringForm.templateKey, 'location', recurringForm.location, () => setRecurringForm({ ...recurringForm, location: '' }));
                      else setRecurringForm({ ...recurringForm, location: v });
                    }}
                  >
                    <option value="">—</option>
                    {(templates[recurringForm.templateKey]?.locationOptions || []).map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                    <option value="__add_new__">+ Add new…</option>
                    {recurringForm.location && <option value="__remove__">— Remove from list</option>}
                  </select>
                </div>
                <div className="form-group">
                  <label>Topic</label>
                  <select
                    value={recurringForm.topic}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__add_new__') handleAddOption('topic', recurringForm.templateKey, (val) => setRecurringForm({ ...recurringForm, topic: val }));
                      else if (v === '__remove__') handleDeleteOption(recurringForm.templateKey, 'topic', recurringForm.topic, () => setRecurringForm({ ...recurringForm, topic: '' }));
                      else setRecurringForm({ ...recurringForm, topic: v });
                    }}
                  >
                    <option value="">—</option>
                    {(templates[recurringForm.templateKey]?.topicOptions || []).map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                    <option value="__add_new__">+ Add new…</option>
                    {recurringForm.topic && <option value="__remove__">— Remove from list</option>}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input
                  type="text"
                  value={recurringForm.notes}
                  onChange={(e) => setRecurringForm({ ...recurringForm, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
              {recurringPreview.length > 0 && (
                <p className="meetings-recurring-preview">
                  {recurringPreview.length} event{recurringPreview.length !== 1 ? 's' : ''}: {recurringPreview.slice(0, 5).map((d) => toDateStr(d)).join(', ')}
                  {recurringPreview.length > 5 && ` … +${recurringPreview.length - 5} more`}
                </p>
              )}
              {error && <p className="meetings-error">{error}</p>}
              <div className="meetings-recurring-actions">
                {editingBatchId ? (
                  <>
                    <button type="submit" disabled={submitting}>{submitting ? 'Updating…' : 'Update series'}</button>
                    <button type="button" onClick={() => { setEditingBatchId(null); setRecurringForm(getDefaultRecurringForm()); }}>Cancel</button>
                  </>
                ) : (
                  <button type="submit" disabled={submitting}>{submitting ? 'Creating…' : 'Create recurring events'}</button>
                )}
              </div>
            </form>

            {batchesLoading && <p className="meetings-loading">Loading series…</p>}
            {!batchesLoading && orphanGroups.length > 0 && (
              <div className="meetings-orphan-groups">
                <h4>Orphan events (no series)</h4>
                <ul>
                  {orphanGroups.map((og) => (
                    <li key={og.signature}>
                      <span>{formatOrphanLabel(og, templates)}</span>
                      <button type="button" onClick={() => handleConvertToSeries(og.signature)} disabled={submitting}>
                        Create as series
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!batchesLoading && batches.length > 0 && (
              <div className="meetings-existing-series">
                <h4>Existing series</h4>
                <ul>
                  {batches.map((b) => (
                    <li key={b.batchId}>
                      <span>{formatBatchLabel(b, templates)}</span>
                      <span className="meetings-series-actions">
                        <button type="button" onClick={() => startEditBatch(b)}>Edit</button>
                        <button type="button" onClick={() => handleDeleteBatch(b.batchId)}>Delete</button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {postponeOpen && (
          <div className="meetings-recurring-content" style={{ marginTop: '0.75rem' }}>
            <form onSubmit={handlePostpone} className="meetings-recurring-form">
              <p className="meetings-manage-hint">
                Shift every meeting on or after the chosen date. Use this when a week changes to a
                modified schedule and everything else needs to slide over.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label>From date</label>
                  <input
                    type="date"
                    value={postponeForm.fromDate}
                    onChange={(e) => setPostponeForm({ ...postponeForm, fromDate: e.target.value })}
                    onClick={(e) => e.target.showPicker?.()}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Direction</label>
                  <select
                    value={postponeForm.direction}
                    onChange={(e) => setPostponeForm({ ...postponeForm, direction: e.target.value })}
                  >
                    <option value="later">Later (postpone)</option>
                    <option value="earlier">Earlier (pull back / undo)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Weeks</label>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={postponeForm.weeks}
                    onChange={(e) => setPostponeForm({ ...postponeForm, weeks: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                  />
                </div>
              </div>
              {postponeForm.direction === 'later' && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input
                      type="checkbox"
                      checked={postponeForm.addModified}
                      onChange={(e) => setPostponeForm({ ...postponeForm, addModified: e.target.checked })}
                    />
                    Add a "Modified Schedule" entry on the freed date
                  </label>
                </div>
              )}
              {postponePreview != null && (
                <p className="meetings-manage-hint">
                  This will move {postponePreview} meeting{postponePreview !== 1 ? 's' : ''} {postponeForm.weeks === 1 ? 'a week' : `${postponeForm.weeks} weeks`} {postponeForm.direction === 'earlier' ? 'earlier' : 'later'}.
                </p>
              )}
              {postponeMessage && <p className="meetings-success">{postponeMessage}</p>}
              {error && <p className="meetings-error">{error}</p>}
              <button type="submit" disabled={submitting}>
                {submitting ? 'Working…' : 'Apply shift'}
              </button>
            </form>
          </div>
        )}
        {manageTemplatesOpen && (
          <div className="meetings-manage-content" style={{ marginTop: '0.75rem' }}>
            {error && <p className="meetings-error">{error}</p>}
            <p className="meetings-manage-hint">
              Edit, delete, or reorder options for Person/Team, Location, and Topic.
            </p>
            {Object.entries(templates).map(([tk, t]) => (
              <div key={tk} className="meetings-manage-template">
                <h4 className="meetings-template-title">
                  {t.badgeColor && <span className="meetings-badge-dot" style={{ backgroundColor: t.badgeColor }} aria-hidden />}
                  {t.label}
                </h4>
                {['personTeam', 'location', 'topic'].map((field) => {
                  const label = { personTeam: 'Person/Team', location: 'Location', topic: 'Topic' }[field];
                  const opts = t[`${field}Options`] || [];
                  return (
                    <div key={field} className="meetings-manage-field">
                      <div className="meetings-manage-field-header">
                        <strong>{label}</strong>
                        <button
                          type="button"
                          className="meetings-manage-sort"
                          onClick={() => handleReorderOptions(tk, field, [...opts].sort((a, b) => a.localeCompare(b)))}
                        >
                          Sort A–Z
                        </button>
                      </div>
                      <ul className="meetings-manage-list">
                        {opts.map((val, idx) => (
                          <li key={val} className="meetings-manage-item">
                            <span className="meetings-manage-value">{val}</span>
                            <span className="meetings-manage-item-actions">
                              <button
                                type="button"
                                className="meetings-manage-btn"
                                title="Edit"
                                onClick={() => handleEditOption(tk, field, val)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="meetings-manage-btn"
                                title="Move up"
                                onClick={() => handleReorderOptions(tk, field, moveOption(opts, idx, 'up'))}
                                disabled={idx === 0}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="meetings-manage-btn"
                                title="Move down"
                                onClick={() => handleReorderOptions(tk, field, moveOption(opts, idx, 'down'))}
                                disabled={idx === opts.length - 1}
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                className="meetings-manage-btn meetings-manage-btn-delete"
                                title="Remove"
                                onClick={() => handleDeleteOption(tk, field, val)}
                              >
                                ✕
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        className="meetings-manage-add"
                        onClick={() => handleAddOption(field, tk)}
                      >
                        + Add {label}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
            )}
            <div className="meetings-events-list-wrap">
            <div className="meetings-events-view">
              <label>
                Archive
                <select
                  value={panel.menu.show.value}
                  onChange={(e) => {
                    setEventsView(parseEventsView(e.target.value));
                    setEditingId(null);
                  }}
                >
                  <option value={panel.menu.show.upcoming.value}>{panel.menu.show.upcoming.label}</option>
                  <optgroup label="Archive">
                    {panel.menu.show.archive.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </optgroup>
                </select>
              </label>
              {panel.menu.week && (
                <label>
                  Week
                  <select
                    value={panel.menu.week.value}
                    onChange={(e) => {
                      setEventsView(parseEventsView(e.target.value));
                      setEditingId(null);
                    }}
                  >
                    {panel.menu.week.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <p className="meetings-manage-hint">{panel.hint}</p>
            {cascadeMessage && <p className="meetings-success">{cascadeMessage}</p>}
            {panel.rows.length === 0 ? (
          <p className="meetings-empty">{panel.emptyText}</p>
        ) : (
          <div className="meetings-list">
            <div className="meetings-list-header">
              <span className="meetings-date">Date</span>
              <span className="meetings-badge">Event</span>
              <span>Person/Team</span>
              <span>Location</span>
              <span>Topic</span>
              <span className="meetings-notes">Notes</span>
              <span className="meetings-actions-header" />
            </div>
            {panel.rows.map((ev) => {
              const accent = templates[ev.templateKey]?.badgeColor || '#4b5563';
              const isToday = toDateStr(ev.date) === cal.today;
              const blockStyle = {
                '--event-accent': accent,
                '--event-bg': hexToRgba(accent, 0.06),
                borderLeft: `4px solid ${accent}`,
                background: isToday ? hexToRgba(accent, 0.1) : hexToRgba(accent, 0.05),
              };
              if (panel.readOnly) {
                return (
                  <div key={ev._id} className="meetings-event-row">
                    <div
                      className="meetings-event-display meetings-event-archived"
                      style={{
                        borderLeft: `4px solid ${accent}`,
                        background: hexToRgba(accent, 0.04),
                      }}
                    >
                      <span className="meetings-event-date">{formatDate(ev.date)}</span>
                      <span className="meetings-event-badge meetings-badge" style={{ backgroundColor: accent }}>
                        {templates[ev.templateKey]?.label || ev.templateKey}
                      </span>
                      <span className="meetings-event-person">{ev.personTeam || '—'}</span>
                      <span className="meetings-event-location">{ev.location || '—'}</span>
                      <span className="meetings-event-topic">{ev.topic || '—'}</span>
                      <span className="meetings-event-notes meetings-notes" title={ev.notes || undefined}>{ev.notes || ''}</span>
                    </div>
                  </div>
                );
              }
              return (
              <div key={ev._id} className="meetings-event-row">
                {editingId === ev._id ? (
                  <form onSubmit={handleEditSave} className="meetings-edit-form" style={blockStyle}>
                    <input
                      type="date"
                      value={editForm.date}
                      min={cal.yearStart}
                      max={cal.yearEnd}
                      onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                      required
                    />
                    <select value={editForm.templateKey} onChange={(e) => setEditForm({ ...editForm, templateKey: e.target.value })}>
                      {Object.entries(templates).map(([k, t]) => (
                        <option key={k} value={k}>{t.label}</option>
                      ))}
                    </select>
                    <select
                      value={editForm.personTeam}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__add_new__') handleAddOption('personTeam', editForm.templateKey, (val) => setEditForm({ ...editForm, personTeam: val }));
                        else if (v === '__remove__') handleDeleteOption(editForm.templateKey, 'personTeam', editForm.personTeam, () => setEditForm({ ...editForm, personTeam: '' }));
                        else setEditForm({ ...editForm, personTeam: v });
                      }}
                    >
                      <option value="">—</option>
                      {(templates[editForm.templateKey]?.personTeamOptions || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                      <option value="__add_new__">+ Add new…</option>
                      {editForm.personTeam && <option value="__remove__">— Remove from list</option>}
                    </select>
                    <select
                      value={editForm.location}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__add_new__') handleAddOption('location', editForm.templateKey, (val) => setEditForm({ ...editForm, location: val }));
                        else if (v === '__remove__') handleDeleteOption(editForm.templateKey, 'location', editForm.location, () => setEditForm({ ...editForm, location: '' }));
                        else setEditForm({ ...editForm, location: v });
                      }}
                    >
                      <option value="">—</option>
                      {(templates[editForm.templateKey]?.locationOptions || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                      <option value="__add_new__">+ Add new…</option>
                      {editForm.location && <option value="__remove__">— Remove from list</option>}
                    </select>
                    <select
                      value={editForm.topic}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__add_new__') handleAddOption('topic', editForm.templateKey, (val) => setEditForm({ ...editForm, topic: val }));
                        else if (v === '__remove__') handleDeleteOption(editForm.templateKey, 'topic', editForm.topic, () => setEditForm({ ...editForm, topic: '' }));
                        else setEditForm({ ...editForm, topic: v });
                      }}
                    >
                      <option value="">—</option>
                      {(templates[editForm.templateKey]?.topicOptions || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                      <option value="__add_new__">+ Add new…</option>
                      {editForm.topic && <option value="__remove__">— Remove from list</option>}
                    </select>
                    <input
                      type="text"
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      placeholder="Notes"
                    />
                    <div className="meetings-row-actions">
                      <button type="submit" className="meetings-btn-save">Save</button>
                      <button type="button" onClick={() => setEditingId(null)}>Cancel</button>
                      <button type="button" className="meetings-btn-delete" onClick={() => handleDelete(ev)}>Delete</button>
                    </div>
                  </form>
                ) : (
                  <div className="meetings-event-display" style={blockStyle} onClick={() => startEdit(ev)}>
                    <span className="meetings-event-date">{formatDate(ev.date)}{isToday && <span className="meetings-today-tag">Today</span>}</span>
                    <span className="meetings-event-badge meetings-badge" style={{ backgroundColor: accent }}>
                      {templates[ev.templateKey]?.label || ev.templateKey}
                    </span>
                    <span className="meetings-event-person">{ev.personTeam || '—'}</span>
                    <span className="meetings-event-location">{ev.location || '—'}</span>
                    <span className="meetings-event-topic">{ev.topic || '—'}</span>
                    <span className="meetings-event-notes meetings-notes">{ev.notes || ''}</span>
                    <span className="meetings-edit-hint">Edit</span>
                  </div>
                )}
              </div>
            );
            })}
          </div>
        )}
            </div>
          </div>
        )}
      </section>

      <HotIssuesSection events={events} />


      <section className="meetings-list-section meetings-step-study-section">
        <button
          type="button"
          className="meetings-manage-toggle"
          onClick={() => setStepStudyOpen((o) => !o)}
        >
          {stepStudyOpen ? '▼' : '▶'} Step Study ({stepStudyCohorts.length})
        </button>
        {stepStudyOpen && (
          <div className="meetings-step-study-content">
            {stepStudyCohorts.length === 0 ? (
              <p className="meetings-empty">No Step Study cohorts yet.</p>
            ) : (
              <div className="meetings-step-study-table">
            <div className="meetings-step-study-header">
              <span className="meetings-step-study-date">Start Date</span>
              <span>Class Type</span>
              <span>Leaders</span>
              <span className="meetings-step-study-num">Lesson</span>
              <span className="meetings-step-study-num">Active</span>
              <span>Issues</span>
              <span>Next Step</span>
              <span className="meetings-step-study-date">Expected Grad</span>
              <span className="meetings-actions-header" />
            </div>
            {stepStudyCohorts.map((c) => (
              <div key={c._id} className="meetings-step-study-row">
                {editingStepStudyId === c._id ? (
                  <form onSubmit={handleEditStepStudySave} className="meetings-step-study-edit-form">
                    <span className="meetings-step-study-readonly">{formatDateShort(c.startDate)}</span>
                    <span className="meetings-step-study-readonly">{c.classType || '—'}</span>
                    <span className="meetings-step-study-readonly">{formatLeaders(c.leader1, c.leader2)}</span>
                    <span className="meetings-step-study-readonly meetings-step-study-num">{c.latestLesson ?? '—'}</span>
                    <span className="meetings-step-study-readonly meetings-step-study-num">{c.currentActive ?? 0}</span>
                    <input
                      type="text"
                      value={stepStudyEditForm.issues}
                      onChange={(e) => setStepStudyEditForm({ ...stepStudyEditForm, issues: e.target.value })}
                      placeholder="Issues"
                    />
                    <input
                      type="text"
                      value={stepStudyEditForm.nextStep}
                      onChange={(e) => setStepStudyEditForm({ ...stepStudyEditForm, nextStep: e.target.value })}
                      placeholder="Next step"
                    />
                    <span className="meetings-step-study-readonly">{formatDateShort(c.expectedGraduationDate)}</span>
                    <div className="meetings-row-actions">
                      <button type="submit" className="meetings-btn-save">Save</button>
                      <button type="button" onClick={() => setEditingStepStudyId(null)}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div className="meetings-step-study-display" onClick={() => startEditStepStudy(c)}>
                    <span className="meetings-step-study-date">{formatDateShort(c.startDate)}</span>
                    <span className="meetings-step-study-class">{c.classType || '—'}</span>
                    <span className="meetings-step-study-leaders">{formatLeaders(c.leader1, c.leader2)}</span>
                    <span className="meetings-step-study-lesson meetings-step-study-num">{c.latestLesson ?? '—'}</span>
                    <span className="meetings-step-study-active meetings-step-study-num">{c.currentActive ?? 0}</span>
                    <span className="meetings-step-study-issues">{c.issues || '—'}</span>
                    <span className="meetings-step-study-next">{c.nextStep || '—'}</span>
                    <span className="meetings-step-study-graduation meetings-step-study-date">{formatDateShort(c.expectedGraduationDate)}</span>
                    <span className="meetings-edit-hint">Edit</span>
                  </div>
                )}
              </div>
            ))}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="meetings-servant-training-section">
        <button
          type="button"
          className="meetings-manage-toggle"
          onClick={() => setServantTrainingOpen((o) => !o)}
        >
          {servantTrainingOpen ? '▼' : '▶'} Servant Training Schedule ({(() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return servantTrainingEntries.filter((e) => {
              const d = new Date(e.date);
              d.setHours(0, 0, 0, 0);
              return d >= today;
            }).length;
          })()})
        </button>
        {servantTrainingOpen && (
          <div className="meetings-servant-training-content">
            <div className="meetings-servant-training-header">
              <label>
                Year:
                <select
                  value={servantTrainingYear}
                  onChange={(e) => setServantTrainingYear(parseInt(e.target.value, 10))}
                >
                  {[2025, 2026, 2027].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={handleGenerateServantTraining}
                disabled={submitting || servantTrainingEntries.length >= 12}
                title={servantTrainingEntries.length >= 12 ? `Schedule for ${servantTrainingYear} already exists. Edit the entries below.` : ''}
              >
                {servantTrainingEntries.length >= 12 ? 'Schedule exists' : 'Create annual calendar'}
              </button>
              <button
                type="button"
                className="meetings-servant-training-refresh"
                onClick={() => {
                  setServantTrainingLoading(true);
                  fetchServantTrainingSchedule(servantTrainingYear).finally(() => setServantTrainingLoading(false));
                }}
                disabled={servantTrainingLoading}
                title="Refresh schedule"
              >
                ↻
              </button>
            </div>
            {(servantTrainingEntries.length >= 12 || servantTrainingMessage) && (
              <p className="meetings-servant-training-hint">
                {servantTrainingMessage || `Schedule for ${servantTrainingYear} already exists. Edit the entries below to make changes.`}
              </p>
            )}
            {servantTrainingLoading && <p className="meetings-loading">Loading schedule…</p>}
            {!servantTrainingLoading && servantTrainingEntries.length > 0 && (
              <div className="meetings-servant-training-list">
                <div className="meetings-servant-training-header-row">
                  <span className="meetings-servant-training-month">Month</span>
                  <span className="meetings-servant-training-date">Date</span>
                  <span>Time</span>
                  <span>Location</span>
                  <span>Topic</span>
                  <span>Trainer</span>
                  <span className="meetings-actions-header" />
                </div>
                {servantTrainingEntries.map((entry) => {
                  const entryDate = new Date(entry.date);
                  entryDate.setHours(0, 0, 0, 0);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isPast = entryDate < today;
                  const timeStr = [entry.startTime || '10:30', entry.endTime || '12:30'].filter(Boolean).join(' – ');
                  return (
                    <div key={entry._id} className={`meetings-servant-training-row ${isPast ? 'meetings-servant-training-row-past' : ''}`}>
                      {editingServantTrainingId === entry._id ? (
                        <form onSubmit={handleSaveServantTrainingEdit} className="meetings-servant-training-edit-form">
                          <span className="meetings-servant-training-readonly">{MONTH_ABBREV[entry.month - 1]}</span>
                          <input
                            type="date"
                            value={servantTrainingEditForm.date || ''}
                            onChange={(e) => setServantTrainingEditForm({ ...servantTrainingEditForm, date: e.target.value })}
                            required
                          />
                          <div className="meetings-servant-training-time-inputs">
                            <input
                              type="time"
                              value={servantTrainingEditForm.startTime || '10:30'}
                              onChange={(e) => setServantTrainingEditForm({ ...servantTrainingEditForm, startTime: e.target.value })}
                            />
                            <span>–</span>
                            <input
                              type="time"
                              value={servantTrainingEditForm.endTime || '12:30'}
                              onChange={(e) => setServantTrainingEditForm({ ...servantTrainingEditForm, endTime: e.target.value })}
                            />
                          </div>
                          <select
                            value={servantTrainingEditForm.location || 'C110'}
                            onChange={(e) => setServantTrainingEditForm({ ...servantTrainingEditForm, location: e.target.value })}
                          >
                            {(templates['servants-mtg-tng']?.locationOptions || []).map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                          <select
                            value={servantTrainingEditForm.topic || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__add_new__') {
                                handleAddOption('topic', 'servants-mtg-tng', (val) =>
                                  setServantTrainingEditForm((prev) => ({ ...prev, topic: val }))
                                );
                              } else {
                                setServantTrainingEditForm((prev) => ({ ...prev, topic: v }));
                              }
                            }}
                          >
                            <option value="">—</option>
                            {(templates['servants-mtg-tng']?.topicOptions || []).map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                            <option value="__add_new__">+ Add new…</option>
                          </select>
                          <select
                            value={servantTrainingEditForm.trainer || 'TBD'}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '__add_new__') {
                                handleAddOption('personTeam', 'servants-mtg-tng', (val) =>
                                  setServantTrainingEditForm((prev) => ({ ...prev, trainer: val }))
                                );
                              } else {
                                setServantTrainingEditForm((prev) => ({ ...prev, trainer: v }));
                              }
                            }}
                          >
                            <option value="TBD">TBD</option>
                            {(templates['servants-mtg-tng']?.personTeamOptions || []).filter((o) => o !== 'TBD').map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                            <option value="__add_new__">+ Add new…</option>
                          </select>
                          <div className="meetings-row-actions">
                            <button type="submit" className="meetings-btn-save" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
                            <button type="button" onClick={() => { setEditingServantTrainingId(null); setServantTrainingEditForm({}); }}>Cancel</button>
                          </div>
                        </form>
                      ) : (
                        <div className="meetings-servant-training-display" onClick={() => startEditServantTraining(entry)}>
                          <span className="meetings-servant-training-month">{MONTH_ABBREV[entry.month - 1]}</span>
                          <span className="meetings-servant-training-date">{formatDateShort(entry.date)}</span>
                          <span className="meetings-servant-training-time">{timeStr}</span>
                          <span className="meetings-servant-training-location">{entry.location || 'C110'}</span>
                          <span className="meetings-servant-training-topic" title={entry.topic || ''}>{entry.topic || '—'}</span>
                          <span className="meetings-servant-training-trainer">{entry.trainer || 'TBD'}</span>
                          <span className="meetings-edit-hint">Edit</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!servantTrainingLoading && servantTrainingEntries.length === 0 && (
              <p className="meetings-empty">
                No schedule for {servantTrainingYear}. Select a different year above, or click &quot;Create annual calendar&quot; to generate one.
              </p>
            )}
          </div>
        )}
      </section>

      <section className="meetings-projects-section">
        <button
          type="button"
          className="meetings-manage-toggle"
          onClick={() => setProjectsOpen((o) => !o)}
        >
          {projectsOpen ? '▼' : '▶'} Projects ({projects.filter((p) => p.status !== 'Completed' && !p.actualCompletionDate).length})
        </button>
        {projectsOpen && (
          <div className="meetings-projects-content">
            <div className="meetings-projects-header">
              <button
                type="button"
                onClick={() => {
                  setProjectsError('');
                  setAddingProject(true);
                  setEditingProjectId(null);
                  setExpandedProjectId(null);
                  setProjectForm(getDefaultProjectForm());
                }}
                disabled={addingProject || !!editingProjectId}
              >
                + Add project
              </button>
              <button
                type="button"
                className="meetings-servant-training-refresh"
                onClick={async () => {
                  setProjectsLoading(true);
                  setProjectsError('');
                  try {
                    await fetchProjects();
                  } finally {
                    setProjectsLoading(false);
                  }
                }}
                disabled={projectsLoading}
                title="Refresh projects"
              >
                ↻
              </button>
            </div>
            {projectsLoading && <p className="meetings-loading">Loading projects…</p>}
            {!projectsLoading && (
              <>
                {(error || projectsError) && <p className="meetings-error">{error || projectsError}</p>}

                {(addingProject || editingProjectId) && (
                  <form onSubmit={addingProject ? handleAddProject : handleUpdateProject} className="meetings-project-form">
                    <div className="meetings-project-form-section">
                      <h4>Basic Project Information</h4>
                      <div className="meetings-project-form-grid">
                        <div className="form-group">
                          <label>Project Name *</label>
                          <input
                            type="text"
                            value={projectForm.projectName || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, projectName: e.target.value })}
                            placeholder="e.g. CR Lesson 9 Inventory Rewrite"
                            required
                          />
                        </div>
                        <div className="form-group form-group-full">
                          <label>Short description</label>
                          <input
                            type="text"
                            value={projectForm.shortDescription || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, shortDescription: e.target.value })}
                            placeholder="Brief description"
                          />
                        </div>
                        <div className="form-group">
                          <label>Ministry Area</label>
                          <select
                            value={projectForm.ministryArea || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, ministryArea: e.target.value })}
                          >
                            <option value="">—</option>
                            <option value="Celebrate Recovery">Celebrate Recovery</option>
                            <option value="Step Study">Step Study</option>
                            <option value="Outreach">Outreach</option>
                            <option value="Training">Training</option>
                            <option value="Events">Events</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Project Type</label>
                          <select
                            value={projectForm.projectType || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, projectType: e.target.value })}
                          >
                            <option value="">—</option>
                            <option value="Event">Event</option>
                            <option value="Lesson">Lesson</option>
                            <option value="System">System</option>
                            <option value="Outreach">Outreach</option>
                            <option value="Facility">Facility</option>
                            <option value="Volunteer Initiative">Volunteer Initiative</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="meetings-project-form-section">
                      <h4>Leadership and Ownership</h4>
                      <div className="meetings-project-form-grid">
                        <div className="form-group">
                          <label>Project Owner</label>
                          <input
                            type="text"
                            value={projectForm.projectOwner || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, projectOwner: e.target.value })}
                            placeholder="Person responsible"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="meetings-project-form-section">
                      <h4>Status Tracking</h4>
                      <div className="meetings-project-form-grid">
                        <div className="form-group">
                          <label>Status</label>
                          <select
                            value={projectForm.status || 'Idea'}
                            onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
                          >
                            <option value="Idea">Idea</option>
                            <option value="Planning">Planning</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Waiting / Blocked">Waiting / Blocked</option>
                            <option value="Completed">Completed</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Priority Level</label>
                          <select
                            value={projectForm.priorityLevel || 'Medium'}
                            onChange={(e) => setProjectForm({ ...projectForm, priorityLevel: e.target.value })}
                          >
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Percent Complete</label>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={projectForm.percentComplete ?? 0}
                            onChange={(e) => setProjectForm({ ...projectForm, percentComplete: parseInt(e.target.value, 10) || 0 })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="meetings-project-form-section">
                      <h4>Timeline</h4>
                      <div className="meetings-project-form-grid">
                        <div className="form-group">
                          <label>Project Start Date</label>
                          <input
                            type="date"
                            value={projectForm.projectStartDate || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, projectStartDate: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Target Completion Date</label>
                          <input
                            type="date"
                            value={projectForm.targetCompletionDate || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, targetCompletionDate: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Actual Completion Date</label>
                          <input
                            type="date"
                            value={projectForm.actualCompletionDate || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, actualCompletionDate: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Next Review Date</label>
                          <input
                            type="date"
                            value={projectForm.nextReviewDate || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, nextReviewDate: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="meetings-project-form-section">
                      <h4>Impact / Ministry Value</h4>
                      <div className="meetings-project-form-grid">
                        <div className="form-group form-group-full">
                          <label>CR Area Impacted</label>
                          <div className="meetings-project-checkboxes">
                            {['Large Group', 'Step Study', 'Newcomers', 'Training', 'Outreach'].map((area) => (
                              <label key={area} className="meetings-project-checkbox">
                                <input
                                  type="checkbox"
                                  checked={(projectForm.crAreaImpacted || []).includes(area)}
                                  onChange={(e) => {
                                    const arr = projectForm.crAreaImpacted || [];
                                    const next = e.target.checked ? [...arr, area] : arr.filter((a) => a !== area);
                                    setProjectForm({ ...projectForm, crAreaImpacted: next });
                                  }}
                                />
                                {area}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="meetings-project-form-section">
                      <h4>Resources</h4>
                      <div className="meetings-project-form-grid">
                        <div className="form-group">
                          <label>Budget Needed</label>
                          <input
                            type="text"
                            value={projectForm.budgetNeeded || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, budgetNeeded: e.target.value })}
                          />
                        </div>
                        <div className="form-group form-group-full">
                          <label>Resources Needed</label>
                          <input
                            type="text"
                            value={projectForm.resourcesNeeded || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, resourcesNeeded: e.target.value })}
                            placeholder="Rooms, materials, tech, etc."
                          />
                        </div>
                        <div className="form-group form-group-full">
                          <label>Outside Support Required</label>
                          <input
                            type="text"
                            value={projectForm.outsideSupportRequired || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, outsideSupportRequired: e.target.value })}
                            placeholder="Church staff, IT, etc."
                          />
                        </div>
                      </div>
                    </div>

                    <div className="meetings-project-form-section">
                      <h4>Communication</h4>
                      <div className="meetings-project-form-grid">
                        <div className="form-group">
                          <label>Last Update Date</label>
                          <input
                            type="date"
                            value={projectForm.lastUpdateDate || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, lastUpdateDate: e.target.value })}
                          />
                        </div>
                        <div className="form-group form-group-full">
                          <label>Next Action Step</label>
                          <input
                            type="text"
                            value={projectForm.nextActionStep || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, nextActionStep: e.target.value })}
                            placeholder="e.g. Finalize acrostic section"
                          />
                        </div>
                        <div className="form-group form-group-full">
                          <label>Notes / Comments</label>
                          <input
                            type="text"
                            value={projectForm.notes || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })}
                            placeholder="e.g. Must align with pastor vision"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="meetings-project-form-section">
                      <h4>Accountability</h4>
                      <div className="meetings-project-form-grid">
                        <div className="form-group form-group-full">
                          <label>Final Outcome</label>
                          <input
                            type="text"
                            value={projectForm.finalOutcome || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, finalOutcome: e.target.value })}
                          />
                        </div>
                        <div className="form-group form-group-full">
                          <label>Lessons Learned</label>
                          <input
                            type="text"
                            value={projectForm.lessonsLearned || ''}
                            onChange={(e) => setProjectForm({ ...projectForm, lessonsLearned: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="meetings-project-form-actions">
                      <button type="submit" disabled={submitting}>
                        {submitting ? 'Saving…' : (addingProject ? 'Add project' : 'Save changes')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAddingProject(false);
                          setEditingProjectId(null);
                          setProjectForm(getDefaultProjectForm());
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {projects.length === 0 && !addingProject && !editingProjectId && (
                  <p className="meetings-empty">No projects yet. Click &quot;+ Add project&quot; to add one.</p>
                )}

                {projects.length > 0 && !addingProject && !editingProjectId && (() => {
                  const isCompleted = (p) => p.status === 'Completed' || !!p.actualCompletionDate;
                  const active = projects.filter((p) => !isCompleted(p));
                  const completed = projects.filter(isCompleted);
                  const completedByYear = {};
                  completed.forEach((p) => {
                    const year = p.actualCompletionDate
                      ? new Date(p.actualCompletionDate).getFullYear()
                      : p.updatedAt ? new Date(p.updatedAt).getFullYear() : new Date().getFullYear();
                    if (!completedByYear[year]) completedByYear[year] = [];
                    completedByYear[year].push(p);
                  });
                  const completedYears = Object.keys(completedByYear).map(Number).sort((a, b) => b - a);
                  const renderProjectRow = (p) => {
                    const isExpanded = expandedProjectId === p._id;
                    const completed = isCompleted(p);
                    return (
                      <div key={p._id} className={`meetings-project-row ${completed ? 'meetings-project-completed' : ''}`}>
                        <div
                          className="meetings-project-row-main"
                          onClick={() => {
                            setExpandedProjectId(isExpanded ? null : p._id);
                          }}
                        >
                          <div className="meetings-project-row-header">
                            <strong>{p.projectName || 'Untitled'}</strong>
                            <span className="meetings-project-status-badge">{p.status || '—'}</span>
                            {completed && (
                              <span className="meetings-project-completed-badge">Completed</span>
                            )}
                            <span className="meetings-edit-hint">{isExpanded ? '▲ Collapse' : '▼ Expand'}</span>
                          </div>
                          <span className="meetings-project-meta">
                            Owner: {p.projectOwner || '—'} • Priority: {p.priorityLevel || '—'} • {p.percentComplete ?? 0}% complete
                          </span>
                          <span className="meetings-project-meta">
                            Start: {p.projectStartDate ? formatDateShort(p.projectStartDate) : '—'} • Target: {p.targetCompletionDate ? formatDateShort(p.targetCompletionDate) : '—'}
                            {p.actualCompletionDate && ` • Completed: ${formatDateShort(p.actualCompletionDate)}`}
                          </span>
                          {isExpanded && (
                            <div className="meetings-project-details">
                              <p><strong>Description:</strong> {p.shortDescription || '—'}</p>
                              <p><strong>Ministry Area:</strong> {p.ministryArea || '—'} • <strong>Type:</strong> {p.projectType || '—'}</p>
                              <p><strong>Next Action Step:</strong> {p.nextActionStep || '—'}</p>
                              <p><strong>CR Area Impacted:</strong> {p.crAreaImpacted?.length ? p.crAreaImpacted.join(', ') : '—'}</p>
                              <p><strong>Budget Needed:</strong> {p.budgetNeeded || '—'}</p>
                              <p><strong>Resources Needed:</strong> {p.resourcesNeeded || '—'}</p>
                              <p><strong>Outside Support:</strong> {p.outsideSupportRequired || '—'}</p>
                              <p><strong>Final Outcome:</strong> {p.finalOutcome || '—'}</p>
                              <p><strong>Lessons Learned:</strong> {p.lessonsLearned || '—'}</p>
                              <p><strong>Notes:</strong> {p.notes || '—'}</p>
                              <button
                                type="button"
                                className="meetings-project-edit-btn"
                                onClick={(e) => { e.stopPropagation(); startEditProject(p); }}
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="meetings-project-delete"
                          onClick={(e) => { e.stopPropagation(); handleDeleteProject(p._id); }}
                          title="Delete project"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  };
                  return (
                    <div className="meetings-projects-list">
                      {active.length > 0 && (
                        <div className="meetings-projects-active">
                          <h4>Active Projects</h4>
                          {active.map(renderProjectRow)}
                        </div>
                      )}
                      {completedYears.length > 0 && (
                        <div className="meetings-projects-archive">
                          <button
                            type="button"
                            className="meetings-manage-toggle meetings-projects-archive-toggle"
                            onClick={() => setCompletedArchiveOpen((o) => !o)}
                          >
                            {completedArchiveOpen ? '▼' : '▶'} Completed (Archived by Year)
                          </button>
                          {completedArchiveOpen && completedYears.map((year) => (
                            <div key={year} className="meetings-projects-year">
                              <h5>{year}</h5>
                              {completedByYear[year].map(renderProjectRow)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </section>

      <section className="meetings-list-section meetings-leader-dev-section">
        <button
          type="button"
          className="meetings-manage-toggle"
          onClick={() => setLeaderDevOpen((o) => !o)}
        >
          {leaderDevOpen ? '▼' : '▶'} Servant Volunteer Development ({leaderDev.length})
        </button>
        {leaderDevOpen && (
          <div className="meetings-leader-dev-content">
            <div className="meetings-leader-dev-header">
              <div className="meetings-leader-dev-filters">
                <button
                  type="button"
                  className={`meetings-leader-dev-filter ${!leaderDevRoleFilter ? 'active' : ''}`}
                  onClick={() => setLeaderDevRoleFilter('')}
                >
                  All
                </button>
                {LEADER_DEV_ROLES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`meetings-leader-dev-filter ${leaderDevRoleFilter === r ? 'active' : ''}`}
                    onClick={() => setLeaderDevRoleFilter(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <div className="meetings-leader-dev-actions">
                <button
                  type="button"
                  onClick={() => {
                    setAddingLeaderDev(true);
                    setEditingLeaderDevId(null);
                    setLeaderDevForm(getDefaultLeaderDevForm());
                  }}
                  disabled={addingLeaderDev || !!editingLeaderDevId}
                >
                  + Add servant volunteer
                </button>
                <button
                  type="button"
                  className="meetings-servant-training-refresh"
                  onClick={() => {
                    setLeaderDevLoading(true);
                    fetchLeaderDevelopment().finally(() => setLeaderDevLoading(false));
                  }}
                  disabled={leaderDevLoading}
                  title="Refresh"
                >
                  ↻
                </button>
              </div>
            </div>
            {error && <p className="meetings-error">{error}</p>}
            {leaderDevLoading && <p className="meetings-loading">Loading…</p>}
            {(addingLeaderDev || editingLeaderDevId) && (
              <form onSubmit={addingLeaderDev ? handleAddLeaderDev : handleUpdateLeaderDev} className="meetings-leader-dev-form">
                <div className="meetings-leader-dev-form-grid">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={leaderDevForm.name || ''}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="e.g. Jason"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Role Being Developed For</label>
                    <select
                      value={leaderDevForm.roleBeingDevelopedFor || ''}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, roleBeingDevelopedFor: e.target.value }))}
                    >
                      <option value="">—</option>
                      {LEADER_DEV_ROLES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={leaderDevForm.status || 'Contacted'}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, status: e.target.value }))}
                    >
                      {LEADER_DEV_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Pipeline Stage</label>
                    <select
                      value={leaderDevForm.pipelineStage || ''}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, pipelineStage: e.target.value }))}
                    >
                      <option value="">—</option>
                      {LEADER_DEV_PIPELINE.map((ps) => (
                        <option key={ps} value={ps}>{ps}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Date Contacted</label>
                    <input
                      type="date"
                      value={leaderDevForm.dateContacted || ''}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, dateContacted: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Training Date</label>
                    <input
                      type="date"
                      value={leaderDevForm.trainingDate || ''}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, trainingDate: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Evaluation Date</label>
                    <input
                      type="date"
                      value={leaderDevForm.evaluationDate || ''}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, evaluationDate: e.target.value }))}
                    />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Mentor / Leader Overseeing</label>
                    <input
                      type="text"
                      value={leaderDevForm.mentorLeaderOverseeing || ''}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, mentorLeaderOverseeing: e.target.value }))}
                      placeholder="e.g. Tony"
                    />
                  </div>
                  <div className="form-group form-group-full">
                    <label>Notes</label>
                    <input
                      type="text"
                      value={leaderDevForm.notes || ''}
                      onChange={(e) => setLeaderDevForm((p) => ({ ...p, notes: e.target.value }))}
                      placeholder="e.g. Strong leadership presence"
                    />
                  </div>
                </div>
                <div className="meetings-leader-dev-form-actions">
                  <button type="submit" disabled={submitting}>{submitting ? 'Saving…' : (addingLeaderDev ? 'Add' : 'Save')}</button>
                  <button type="button" onClick={() => { setAddingLeaderDev(false); setEditingLeaderDevId(null); setLeaderDevForm(getDefaultLeaderDevForm()); }}>Cancel</button>
                </div>
              </form>
            )}
            {!leaderDevLoading && leaderDev.length === 0 && !addingLeaderDev && !editingLeaderDevId && (
              <p className="meetings-empty">No servant volunteers in development yet. Click &quot;+ Add servant volunteer&quot; to add one.</p>
            )}
            {!leaderDevLoading && leaderDev.length > 0 && !addingLeaderDev && !editingLeaderDevId && (
              <div className="meetings-leader-dev-list">
                <div className="meetings-leader-dev-header-row">
                  <span>Name</span>
                  <span>Role</span>
                  <span>Status</span>
                  <span>Pipeline</span>
                  <span>Date Contacted</span>
                  <span>Training</span>
                  <span>Evaluation</span>
                  <span>Mentor</span>
                  <span className="meetings-actions-header" />
                </div>
                {sortedLeaderDev.map((entry) => (
                  <div key={entry._id} className="meetings-leader-dev-row">
                    {editingLeaderDevId === entry._id ? (
                      <form onSubmit={handleUpdateLeaderDev} className="meetings-leader-dev-edit-form">
                        <input type="text" value={leaderDevForm.name || ''} onChange={(e) => setLeaderDevForm((p) => ({ ...p, name: e.target.value }))} required />
                        <select value={leaderDevForm.roleBeingDevelopedFor || ''} onChange={(e) => setLeaderDevForm((p) => ({ ...p, roleBeingDevelopedFor: e.target.value }))}>
                          <option value="">—</option>
                          {LEADER_DEV_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <select value={leaderDevForm.status || 'Contacted'} onChange={(e) => setLeaderDevForm((p) => ({ ...p, status: e.target.value }))}>
                          {LEADER_DEV_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={leaderDevForm.pipelineStage || ''} onChange={(e) => setLeaderDevForm((p) => ({ ...p, pipelineStage: e.target.value }))}>
                          <option value="">—</option>
                          {LEADER_DEV_PIPELINE.map((ps) => <option key={ps} value={ps}>{ps}</option>)}
                        </select>
                        <input type="date" value={leaderDevForm.dateContacted || ''} onChange={(e) => setLeaderDevForm((p) => ({ ...p, dateContacted: e.target.value }))} />
                        <input type="date" value={leaderDevForm.trainingDate || ''} onChange={(e) => setLeaderDevForm((p) => ({ ...p, trainingDate: e.target.value }))} />
                        <input type="date" value={leaderDevForm.evaluationDate || ''} onChange={(e) => setLeaderDevForm((p) => ({ ...p, evaluationDate: e.target.value }))} />
                        <input type="text" value={leaderDevForm.mentorLeaderOverseeing || ''} onChange={(e) => setLeaderDevForm((p) => ({ ...p, mentorLeaderOverseeing: e.target.value }))} placeholder="Mentor" />
                        <div className="meetings-row-actions">
                          <button type="submit" className="meetings-btn-save" disabled={submitting}>Save</button>
                          <button type="button" onClick={() => { setEditingLeaderDevId(null); setLeaderDevForm(getDefaultLeaderDevForm()); }}>Cancel</button>
                          <button type="button" className="meetings-btn-delete" onClick={() => handleDeleteLeaderDev(entry._id)}>Delete</button>
                        </div>
                      </form>
                    ) : (
                      <div className="meetings-leader-dev-display" onClick={() => startEditLeaderDev(entry)}>
                        <span className="meetings-leader-dev-name">{entry.name || '—'}</span>
                        <span className="meetings-leader-dev-role">{entry.roleBeingDevelopedFor || '—'}</span>
                        <span className="meetings-leader-dev-status">{entry.status || '—'}</span>
                        <span className="meetings-leader-dev-pipeline">{entry.pipelineStage || '—'}</span>
                        <span className="meetings-leader-dev-contacted">{entry.dateContacted ? formatDateShort(entry.dateContacted) : '—'}</span>
                        <span className="meetings-leader-dev-training">{entry.trainingDate ? formatDateShort(entry.trainingDate) : '—'}</span>
                        <span className="meetings-leader-dev-evaluation">{entry.evaluationDate ? formatDateShort(entry.evaluationDate) : '—'}</span>
                        <span className="meetings-leader-dev-mentor">{entry.mentorLeaderOverseeing || '—'}</span>
                        <span className="meetings-edit-hint">Edit</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default MeetingsPage;
